import React, { ChangeEvent, useRef } from 'react';
import styled from 'styled-components/macro';
import Breakpoints from 'themes/constants/breakpoints';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as XIconSVG } from 'media/icons/x.svg';
import get from 'lodash.get';
import { PORTALS } from 'portals/portals';
import { closeModal } from 'redux/actions/static/modalActions';
import { IRootState } from 'constants/types';
import { States } from 'constants/states';
import useTranslate from 'hooks/useTranslate';
import Portal from 'components/Portal/Portal';
import Button, { ButtonThemeType } from 'components/Button/Button';

// Styles
const Popup = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    transform: translate(0, 0);
    width: 100%;
    border-radius: 20px 20px 0 0;
    background: ${({ theme }) => theme.palette.white};
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
    padding: 42px 30px 14px;
    color: ${({ theme }) => theme.palette.black};
    overflow: hidden;
    z-index: 10000;
    @media only screen and (${Breakpoints.LAPTOP}) {
        top: 50%;
        left: 50%;
        bottom: unset;
        transform: translate(-50%, -50%);
        width: 340px;
        min-height: 210px;
        border-radius: 20px;
    }
`;
const ButtonsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    button {
        font-size: 16px;
        font-weight: 700;
        width: 140px;
        height: 46px;
        line-height: 24px;
        padding: 0;
        &:not(:last-child) {
            margin-bottom: 10px;
        }
    }
`;
const ClosePopup = styled.div`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    right: 0;
    width: 50px;
    height: 50px;
    cursor: pointer;
    & > span {
        display: flex;
        align-items: center;
        justify-content: center;
        background: #c2c2c2;
        width: 16px;
        height: 16px;
        border-radius: 100%;
        & > svg {
            color: ${({ theme }) => theme.palette.white};
            width: 50%;
            height: 50%;
        }
    }
`;
const Title = styled.h3`
    font-size: 16px;
    font-weight: 800;
    line-height: 20px;
    text-align: center;
    color: inherit;
    margin-bottom: 26px;
`;
const Blackout = styled.div`
    z-index: 10000;
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
`;

// Component
const Modal = () => {
  const [getText] = useTranslate();

  const dispatch = useDispatch();

  const modalState = useSelector((state: IRootState) => get(state, [States.MODAL]));

  const blackoutRef = useRef<HTMLDivElement>(null);

  const resolveBtnText = getText(['modal', 'resolveBtn']);
  const rejectBtnText = getText(['modal', 'rejectBtn']);

  const handleClose = () => dispatch(closeModal());

  const handleResolve = () => {
    modalState.onSuccess();
    handleClose();
  };

  const handleClickBlackout = (e: ChangeEvent<any>) => {
    if (e.target === blackoutRef.current) {
      handleClose();
    }
  };

  if (modalState.isOpen) {
    return (
      <Portal selector={PORTALS.MODAL}>
        <Blackout onClick={handleClickBlackout} ref={blackoutRef}>
          <Popup>
            <ClosePopup onClick={handleClose}>
              <span>
                <XIconSVG />
              </span>
            </ClosePopup>
            <Title>{modalState.title}</Title>
            <ButtonsWrapper>
              <Button onClick={handleClose}>
                {rejectBtnText}
              </Button>
              <Button
                themeType={ButtonThemeType.MINOR}
                onClick={handleResolve}
              >
                {resolveBtnText}
              </Button>
            </ButtonsWrapper>
          </Popup>
        </Blackout>
      </Portal>
    );
  }
  return null;
};

export default Modal;
